/**
 * The Renewals list's Status control. Mirrors `Renewal.STATUS_CHOICES`
 * (backend/apps/renewals/models.py) exactly — the same house rule as
 * `pages/reports/reportStatusOptions.ts`.
 *
 * Found while fixing queue #246. The list offered three values that are not
 * Renewal statuses at all:
 *   - `due` and `in_progress` — `RenewalViewSet.get_queryset` filters
 *     `status=<value>` verbatim, so picking either returned an empty list with
 *     no error: a control that looks like it works and narrows to nothing.
 *   - `upcoming` — a *second*, differently-defined Upcoming control on the same
 *     page as the Upcoming badge. It is the dashboard widget's synthetic value:
 *     capped at 30 days, and because it takes its own branch it skips the
 *     renewed/cancelled exclusion, so renewed rows come back. Two "Upcoming"
 *     controls on one page that disagree is the confusion #246 reports. The
 *     badge (`days_to_expiry=upcoming`) is the one that matches the counts.
 * The dashboard's own upcoming-renewals widget is unaffected — it builds its
 * request in `pages/dashboard/DashboardPage.tsx`, not from this list.
 *
 * B9: `status` on the queue (`GET /renewals/queue/`) filters the LATEST
 * Renewal row's status, same as before, but a policy can now be in the queue
 * with none yet -- the workflow only starts on the first assign / status
 * update / activity log. `none` is the queue's sentinel for that case (§2.2),
 * not a `Renewal.STATUS_CHOICES` value, so it is listed separately from the
 * real statuses below.
 */

export const renewalStatusOptions = [
  { value: '', label: 'All Status' },
  { value: 'none', label: 'Not started' },
  { value: 'pending', label: 'Pending' },
  { value: 'reminder_sent', label: 'Reminder Sent' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'quote_requested', label: 'Quote Requested' },
  { value: 'quote_sent', label: 'Quote Sent' },
  { value: 'in_negotiation', label: 'In Negotiation' },
  { value: 'payment_pending', label: 'Payment Pending' },
  { value: 'renewed', label: 'Renewed' },
  { value: 'lapsed', label: 'Lapsed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'ported', label: 'Ported to Another Insurer' },
  { value: 'not_interested', label: 'Not Interested' },
];

/**
 * B9: the label for a queue row's WORKFLOW status. A policy with no Renewal
 * row yet is "Not started" -- never "Pending". The row's convenience `status`
 * field defaults to `pending` in that case, but the Status filter's Pending
 * option sends `status=pending` (workflow_status = pending), which does NOT
 * match those rows; they are matched by `status=none` ("Not started"). Showing
 * "Pending" made the Pending filter hide every row badged Pending. List,
 * detail and the contact tab all render through this one function so they
 * cannot disagree about the same policy.
 */
export function renewalWorkflowStatusLabel(workflowStatus: string | null | undefined): string {
  if (!workflowStatus) return 'Not started';
  const option = renewalStatusOptions.find((o) => o.value === workflowStatus && o.value !== '' && o.value !== 'none');
  if (option) return option.label;
  return workflowStatus
    .split('_')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}
